import { useState } from 'react';
import { Box, Card, Chip, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

import { useOrganizationUpdaterProvider } from '../../../context_providers/organization_context/OrganizationProvider';
import { StringKeys } from '@/resources/strings/StringKeys';
import COLORS from '@/resources/style/Themes';
import {
  buildPoolCounts,
  getPoolPurchased,
} from '@/utils/organization/orgSeatAvailability';
import { getTierLabelKey } from '@/utils/entitlements/tierLabels';
import {
  getTierChipSx,
  type TierChipVariant,
} from '@/utils/entitlements/tierChipStyles';
import { SubscriptionTier } from '@/../generated-client/models';
import TierLearnMoreModal, {
  type TierLearnMoreVariant,
} from './modals/TierLearnMoreModal';

type SummaryCardProps = {
  variant: TierChipVariant;
  chipLabel: string;
  value: number;
  description: string;
  showLearnMore?: boolean;
  onLearnMoreClick?: () => void;
};

const SummaryCard = ({
  variant,
  chipLabel,
  value,
  description,
  showLearnMore,
  onLearnMoreClick,
}: SummaryCardProps) => (
  <Card
    elevation={0}
    sx={{
      backgroundColor: COLORS.WHITE,
      boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.15)',
      borderRadius: 3,
      p: 3,
      flex: 1,
      minWidth: 200,
    }}
  >
    <Chip
      label={chipLabel}
      size="small"
      sx={{
        ...getTierChipSx(variant),
        mb: 2,
      }}
    />

    <Typography
      sx={{
        fontSize: 32,
        fontWeight: 500,
        color:
          variant === 'respond'
            ? COLORS.DARK_GREEN
            : variant === 'ops'
              ? COLORS.SAFEGUARD_BLUE
              : COLORS.CONNECT_TEXT_GRAY,
        lineHeight: 1.2,
        mb: 1,
      }}
    >
      {value}
    </Typography>

    <Typography sx={{ fontSize: 14, color: COLORS.CONNECT_TEXT_GRAY }}>
      {description}
    </Typography>

    {showLearnMore && <LearnMoreLink onClick={onLearnMoreClick} />}
  </Card>
);

const LearnMoreLink = ({ onClick }: { onClick?: () => void }) => {
  const { t } = useTranslation();

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: 13,
        color: COLORS.GREY,
        mt: 1.5,
        cursor: 'pointer',
        '&:hover': { color: COLORS.SAFEGUARD_BLUE },
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 15 }} />
      <Typography component="span" sx={{ fontSize: 13 }}>
        {t(StringKeys.LEARN_MORE)}
      </Typography>
    </Box>
  );
};

const SectionHeader = ({
  title,
  tooltip,
}: {
  title: string;
  tooltip: string;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
    <Typography sx={{ fontSize: 15, color: COLORS.SAFEGUARD_BLUE }}>
      {title}
    </Typography>
    <Tooltip title={tooltip} arrow placement="top">
      <InfoOutlinedIcon sx={{ fontSize: 16, color: COLORS.GREY }} />
    </Tooltip>
  </Box>
);

const OrganizationSubscriptions = () => {
  const { t } = useTranslation();
  const { organization } = useOrganizationUpdaterProvider();
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);

  const [learnMoreVariant, setLearnMoreVariant] =
    useState<TierLearnMoreVariant>('connect');

  const openLearnMore = (variant: TierLearnMoreVariant) => {
    setLearnMoreVariant(variant);
    setLearnMoreOpen(true);
  };

  if (!organization) {
    return null;
  }

  const orgPoolCounts = buildPoolCounts(organization);
  const opsPurchased = getPoolPurchased(orgPoolCounts, SubscriptionTier.Ops);
  const respondPurchased = getPoolPurchased(orgPoolCounts, SubscriptionTier.Respond);
  const connectUsed = orgPoolCounts.connect_seats_used ?? 0;
  const opsUsed = orgPoolCounts.ops_seats_used ?? 0;
  const respondUsed = orgPoolCounts.respond_seats_used ?? 0;

  const connectLabel = getTierLabelKey(SubscriptionTier.Connect);
  const opsLabel = getTierLabelKey(SubscriptionTier.Ops);
  const respondLabel = getTierLabelKey(SubscriptionTier.Respond);

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Typography sx={{ fontSize: 14, color: COLORS.GREY, fontWeight: 500 }}>
        {t(StringKeys.SUBSCRIPTIONS)}
      </Typography>

      <Typography
        variant="h5"
        sx={{ color: COLORS.SAFEGUARD_BLUE, fontWeight: 500, mb: 3 }}
      >
        {organization.company_name}
      </Typography>

      <SectionHeader
        title={t(StringKeys.TOTAL_SUBSCRIPTIONS_AVAILABLE)}
        tooltip={t(StringKeys.TOTAL_SUBSCRIPTIONS_TOOLTIP)}
      />

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mb: 4 }}>
        <SummaryCard
          variant="ops"
          chipLabel={opsLabel}
          value={opsPurchased}
          description={t(StringKeys.TOTAL_OPS_SUBSCRIPTIONS_TOOLTIP)}
        />
        <SummaryCard
          variant="respond"
          chipLabel={respondLabel}
          value={respondPurchased}
          description={t(StringKeys.TOTAL_RESPOND_SUBSCRIPTIONS_TOOLTIP)}
        />
      </Stack>

      <SectionHeader
        title={t(StringKeys.USAGE_ACROSS_YOUR_ORGANIZATION)}
        tooltip={t(StringKeys.USAGE_ACROSS_ORGANIZATION_TOOLTIP)}
      />

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
        <SummaryCard
          variant="connect"
          chipLabel={connectLabel}
          value={connectUsed}
          description={t(StringKeys.USERS_WITH_UNPAID_ACCOUNTS)}
          showLearnMore
          onLearnMoreClick={() => openLearnMore('connect')}
        />
        <SummaryCard
          variant="ops"
          chipLabel={opsLabel}
          value={opsUsed}
          description={t(StringKeys.USERS_ALLOCATED_TO_OPS_TIER)}
          showLearnMore
          onLearnMoreClick={() => openLearnMore('ops')}
        />
        <SummaryCard
          variant="respond"
          chipLabel={respondLabel}
          value={respondUsed}
          description={t(StringKeys.USERS_ALLOCATED_TO_RESPOND_TIER)}
          showLearnMore
          onLearnMoreClick={() => openLearnMore('respond')}
        />
      </Stack>

      <TierLearnMoreModal
        open={learnMoreOpen}
        onClose={() => setLearnMoreOpen(false)}
        variant={learnMoreVariant}
      />
    </Box>
  );
};

export default OrganizationSubscriptions;
